import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { ITextField, TextField } from '@fluentui/react/lib/TextField';
import { KeyCodes } from '@fluentui/react/lib/Utilities';

/**
 * Edit node properties
 * className: CSS class name
 * onEntry: handler to call on entering new text
 * onDismiss: handler for dismissing text box
 * defaultValue: default value to show on activation
 */
export interface CalendarNodeTextFieldProps {
    className: string;
    onEntry: (value: string) => void;
    onDismiss: () => void;
    defaultValue?: string;
    ariaLabel?: string;
}

/**
 * Component that renders a TextField that is focused when mounted,
 * and issues a callback with the current value when either blurred or
 * "Enter" is pressed.
 */
export default observer(function CalendarNodeTextField(props: CalendarNodeTextFieldProps) {
    React.useEffect(() => {
        textField.current.focus();
        textField.current.select();
    }, []);
    const textField = React.useRef<ITextField>();
    const refTextField = (input: any) => {
        textField.current = input;
    };
    const onBlur = (evt: React.FocusEvent<HTMLElement>) => {
        if (!(evt.currentTarget as HTMLElement).contains(document.activeElement)) {
            props.onEntry(textField.current.value);
        }
    };
    const onKeyDown = (evt: React.KeyboardEvent<unknown>) => {
        switch (evt.which) {
            case KeyCodes.enter:
                // prevent 'enter' to select the calendar item
                evt.preventDefault();
                props.onEntry(textField.current.value);
                break;
            case KeyCodes.escape:
                props.onDismiss();
                break;
        }
    };
    return (
        <div className={props.className}>
            <TextField
                onClick={onClick}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                defaultValue={props.defaultValue}
                componentRef={refTextField}
                ariaLabel={props.ariaLabel}
            />
        </div>
    );
});

function onClick(evt: React.MouseEvent<unknown>) {
    // prevent clicks from propagating to calendar behind
    evt.stopPropagation();
}
